import {
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import { MdFeedback } from "react-icons/md";
import { useEffect, useState } from "react";
import { DocumentType, gql } from "__generated__/gql";
import TooltipIconButton from "components/Shared/TooltipIconButton";
import { navBarTooltipIconButtonProps } from "components/Layout/NavBar/TooltipIconLinkButton";
import { asyncVoid } from "utils/asyncVoidHandler";
import type { DocumentVariablesType } from "utils/apollo/hooks";
import { useCurrentUserData } from "utils/currentUserHooks";
import { openFeedbackForm } from "utils/userbackContext";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const USERBACK_POPOVER_METADATA_KEY = "hasSeenUserbackFeedbackPopover";

const mutation = gql(/* GraphQL */ `
  mutation UpdateFeedbackPopoverState($userId: UUID!, $metadataPatch: JSON!) {
    patchUserMetadata(
      input: { userId: $userId, metadataPatch: $metadataPatch }
    ) {
      user {
        id
        metadata
      }
    }
  }
`);

export default function FeedbackButton() {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [updatePopoverState] = useMutation<
    DocumentType<typeof mutation>,
    DocumentVariablesType<typeof mutation>
  >(mutation);

  const { showErrorToast } = useAsyncCompleteToast();

  const userData = useCurrentUserData();
  const { id: userId, metadata: userMetadata = {} } = userData || {};
  const hasSeenFeedbackPopover =
    userMetadata?.[USERBACK_POPOVER_METADATA_KEY] === true;

  useEffect(() => {
    if (!userId || hasSeenFeedbackPopover || isPopoverOpen) return;
    const timeout = window.setTimeout(() => {
      setIsPopoverOpen(true);
    }, 500);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [userId, hasSeenFeedbackPopover, isPopoverOpen]);

  const closePopover = () => {
    setIsPopoverOpen(false);
    const metadataPatch = { [USERBACK_POPOVER_METADATA_KEY]: true };
    if (userId && !hasSeenFeedbackPopover) {
      asyncVoid(updatePopoverState({ variables: { userId, metadataPatch } }));
    }
  };

  return (
    <Popover
      isOpen={isPopoverOpen}
      onClose={closePopover}
      placement="bottom"
      closeOnBlur
      closeOnEsc
    >
      <PopoverTrigger>
        <TooltipIconButton
          label="Send Feedback"
          icon={<Icon as={MdFeedback} />}
          {...navBarTooltipIconButtonProps}
          onClick={() => {
            openFeedbackForm().catch(showErrorToast);
          }}
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="16rem" fontSize="sm">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverBody lineHeight="normal" textAlign="center" pr={10}>
            Have a question, suggestion, or want to report an issue? Share your
            feedback with us here!
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
